import mapboxgl from 'mapbox-gl'

import {
  LONGITUDE_MAX,
  LONGITUDE_MIN,
  LATITUDE_MAX,
  LATITUDE_MIN,
  M2KM,
  EARTH_RADIUS,
  DEG2RAD
} from './consts'

import { isValidPgnValue } from '../core/pgn/utils'
import { PgnGnssData1 } from '../core/pgn/types'
import { cross, dot, normalize, subtract, Vector3 } from './vector'

export type LatitudeGetter<T> = (x: T) => number
export type LongitudeGetter<T> = (x: T) => number
export type GeoJsonPropertiesGetter<TArgs extends any[]> = (...args: TArgs) => GeoJSON.GeoJsonProperties

export interface Point {
  lng: number  // longitude in degrees
  lat: number, // latitude in degrees
}

export interface Line {
  start: Point,
  end: Point
}

export type BoundingBox = [
  west: number,
  south: number,
  east: number,
  north: number
]

export const isValidLat = (num: number): boolean => isValidPgnValue(num) && num >= LATITUDE_MIN && num <= LATITUDE_MAX
export const isValidLon = (num: number): boolean => isValidPgnValue(num) && num >= LONGITUDE_MIN && num <= LONGITUDE_MAX

// Retrieves the users current location via the browsers Geolocation API. Returns a Promise with a GeolocationPosition object as it's result.
export const getCurrentUserPosition = (): Promise<GeolocationPosition> => {
  return new Promise<GeolocationPosition>((resolve: PositionCallback, reject: PositionErrorCallback) => {
    if (('geolocation' in navigator)) {
      navigator.geolocation.getCurrentPosition(resolve, reject)
    } else {
      const msg = 'Geolocation API is not available'
      console.error(msg)

      var err: GeolocationPositionError = {
        code: GeolocationPositionError.POSITION_UNAVAILABLE,
        message: msg,

        // These are included for compatibility
        PERMISSION_DENIED: 1,
        POSITION_UNAVAILABLE: 2,
        TIMEOUT: 3,
      }

      reject(err)
    }
  })
}

// Creates a GeoJSON feature collection with the given featrues.
export const createGeoJsonFC = (features: GeoJSON.Feature[] = []): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features
})

// Converts an array to a GeoJSON FeatureCollection of LineStrings. Makes no assumtions about the shape of the objects.
export const arrayToGeoJsonLSFC = <T>(array: T[], getLat: LatitudeGetter<T>, getLon: LongitudeGetter<T>, getProperties: GeoJsonPropertiesGetter<[T, T]> | null = null): GeoJSON.FeatureCollection => {
  const comparePos = (pos1: Point, pos2: Point): boolean => pos1.lat === pos2.lat && pos1.lng === pos2.lng

  const data: Point[] = []
  for (let i = 0; i < array.length; i++) {
    const curr = array[i]

    if (!!curr) {
      data.push({
        lng: getLon(curr),
        lat: getLat(curr)
      })
    }
  }

  const features: GeoJSON.Feature[] = []
  let prevPos = data[0]
  for (let i = 1; i < data.length; i++) {
    // When encountering a string of same coordinates take only the last one
    while (i < data.length - 1 && comparePos(prevPos, data[i])) i++

    const currPos = data[i]
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [[prevPos.lng, prevPos.lat], [currPos.lng, currPos.lat]]
      },
      properties: !!getProperties ? getProperties(array[i - 1], array[i]) || {} : {}
    })

    prevPos = currPos
  }

  return createGeoJsonFC(features)
}

export const gnss1DataToGeoJsonLSFC = (array: PgnGnssData1[], getProperties: GeoJsonPropertiesGetter<[PgnGnssData1, PgnGnssData1]> | null = null) => {
  return arrayToGeoJsonLSFC(
    array.filter(d => isValidLat(d.Lat) && isValidLon(d.Lon)),
    x => x.Lat,
    x => x.Lon,
    getProperties
  )
}

export const convertoToMapboxLngLat = <T>(array: T[], getLat: LatitudeGetter<T>, getLon: LongitudeGetter<T>): mapboxgl.LngLatLike[] => {
  return array.map(x => ({
    lat: getLat(x),
    lng: getLon(x)
  }))
}

export const convertGnss1DataToMapboxLngLat = (array: PgnGnssData1[]): mapboxgl.LngLatLike[] => {
  return convertoToMapboxLngLat(
    array,
    x => x.Lat,
    x => x.Lon
  )
}


/**
 * Recursively extracts all coordinates from a GeoJSON geometry
 */
const extractCoordinates = (geometry: GeoJSON.Geometry): number[][] => {
  const coords: number[][] = []

  switch (geometry.type) {
    case 'Point':
      coords.push(geometry.coordinates)
      break
    case 'MultiPoint':
    case 'LineString':
      coords.push(...geometry.coordinates)
      break
    case 'MultiLineString':
    case 'Polygon':
      for (const ring of geometry.coordinates) {
        coords.push(...ring)
      }
      break
    case 'MultiPolygon':
      for (const polygon of geometry.coordinates) {
        for (const ring of polygon) {
          coords.push(...ring)
        }
      }
      break
    case 'GeometryCollection':
      for (const geom of geometry.geometries) {
        coords.push(...extractCoordinates(geom))
      }
      break
  }

  return coords
}

/**
 * Calculates bounding box from a single GeoJSON object
 */
const getGeoJsonBounds = (geoJson: GeoJSON.GeoJSON): BoundingBox => {
  let coords: number[][] = []

  if (geoJson.type === 'FeatureCollection') {
    for (const feature of geoJson.features) {
      if (feature.geometry) {
        coords.push(...extractCoordinates(feature.geometry))
      }
    }
  } else if (geoJson.type === 'Feature') {
    if (geoJson.geometry) {
      coords = extractCoordinates(geoJson.geometry)
    }
  } else {
    // It's a Geometry object
    coords = extractCoordinates(geoJson as GeoJSON.Geometry)
  }

  return getBounds(coords, c => c[0], c => c[1]);
}

/*
  Determines the geographical bounds of the given array of GeoJSON objects and returns them. Can be directly used with Mapbox's fitBounds method.
  RETURNS: [west: number, south: number, east: number, north: number]
*/
export const getGeoJsonArrayBounds = (geoJsonArray: GeoJSON.GeoJSON[]): BoundingBox => {
  const boundsArr = geoJsonArray.map(geoJson => getGeoJsonBounds(geoJson))

  let initial: BoundingBox = [
    Infinity, // west
    Infinity, // south
    -Infinity, // east
    -Infinity // north
  ]

  return boundsArr.reduce((acc, b) => [
    Math.min(acc[0], b[0]),
    Math.min(acc[1], b[1]),
    Math.max(acc[2], b[2]),
    Math.max(acc[3], b[3]),
  ], initial)
}

export const getBounds = <T>(points: T[], getLon: LongitudeGetter<T>, getLat: LatitudeGetter<T>): BoundingBox => {
  let north = -Infinity
  let south = Infinity
  let east = -Infinity
  let west = Infinity

  if (!points)
    points = []

  for (const p of points) {
    const lng = getLon(p)
    const lat = getLat(p)

    if (lat > north)
      north = lat

    if (lat < south)
      south = lat

    if (lng > east)
      east = lng

    if (lng < west)
      west = lng
  }

  return [
    west,
    south,
    east,
    north
  ]
}

export const createCircle = (latitude: number, longitude: number, radius: number, vertexCount: number = 32, properties: GeoJSON.GeoJsonProperties = {}): GeoJSON.Feature => {
  const distanceX = (radius * M2KM) / (111.320 * Math.cos(latitude * Math.PI / 180))
  const distanceY = (radius * M2KM) / 110.574

  const pointArray = []
  for (let i = 0; i < vertexCount; i++) {
    const theta = (i / vertexCount) * (2 * Math.PI)
    const x = distanceX * Math.cos(theta)
    const y = distanceY * Math.sin(theta)

    pointArray.push([longitude + x, latitude + y])
  }
  pointArray.push(pointArray[0])

  return {
    type: 'Feature',
    properties: {
      ...properties
    },
    geometry: {
      type: 'Polygon',
      coordinates: [pointArray]
    }
  }
}

/**
 * Projects a latitude and longitude using web mercator projection. The returned coordinates are in meters.
 *
 * @param lng Longitude
 * @param lat Latitude
 * @returns An X,Y point in meter coordinates
 */
export const projectWebMercator = (lng: number, lat: number): { x: number, y: number } => {
  const x = EARTH_RADIUS * lng * Math.PI / 180
  const y = EARTH_RADIUS * Math.log(Math.tan((Math.PI / 4) + (lat * Math.PI / 360)))

  return { x, y }
};

/**
 * Converts the given pixel distance to meters.
 * 
 * @param pixelCount Pixel distance to convert to meters
 */
export const convertPixelDistance = (map: mapboxgl.Map, pixelCount: number): number => {
  const origin = map.unproject(new mapboxgl.Point(0, 0))
  const other = map.unproject(new mapboxgl.Point(pixelCount, 0))

  return getGeoDistance(origin.lng, origin.lat, other.lng, other.lat)
}

/**
 * Function returns distance between two geographical points in meters.
 *
 * @param {number} lon1 longitude of the first point in degrees
 * @param {number} lat1 latitude of the first point in degrees
 * @param {number} lon2 longitude of the second point in degrees
 * @param {number} lat2 latitude of the second point in degrees
 * @returns {number} distance between the point in meters
 */
export function getGeoDistance(lon1: number, lat1: number, lon2: number, lat2: number): number {
  const dLat = (lat2 - lat1) * DEG2RAD
  const dLon = (lon2 - lon1) * DEG2RAD

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * DEG2RAD) * Math.cos(lat2 * DEG2RAD) * Math.sin(dLon / 2) * Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return (EARTH_RADIUS * c); // Distance in m
}

/**
 * Converts lat/lon to 3D Cartesian (ECEF) coordinates
 */
const lonLatToCartesian = (lon: number, lat: number): Vector3 => {
  const latRad = lat * DEG2RAD
  const lonRad = lon * DEG2RAD
  const cosLat = Math.cos(latRad)
  return {
    x: cosLat * Math.cos(lonRad),
    y: cosLat * Math.sin(lonRad),
    z: Math.sin(latRad)
  }
}

/**
 * Converts 3D Cartesian coordinates to lat/lon
 */
const cartesianToLatLon = (v: Vector3): { lon: number; lat: number } => {
  const lon = Math.atan2(v.y, v.x) / DEG2RAD
  const lat = Math.atan2(v.z, Math.sqrt(v.x * v.x + v.y * v.y)) / DEG2RAD
  return { lon, lat }
}

/**
 * Finds the closest point on a great circle line segment between two points on Earth's surface.
 */
export function getClosestPointOnGeoLine(
  point: Point,
  line: Line
): { point: Point, distance: number } {

  const {
    start: lineStart,
    end: lineEnd
  } = line

  const p = lonLatToCartesian(point.lng, point.lat)
  const a = lonLatToCartesian(lineStart.lng, lineStart.lat)
  const b = lonLatToCartesian(lineEnd.lng, lineEnd.lat)

  // Degenerate case: start == end
  if (Math.abs(dot(a, b) - 1.0) < 1e-10) {
    const distance = getGeoDistance(point.lng, point.lat, lineStart.lng, lineStart.lat)
    return { point: lineStart, distance }
  }

  const planeNormal = cross(a, b)

  // Project p onto plane
  const dotPN = dot(p, planeNormal)
  const proj = subtract(p, {
    x: dotPN * planeNormal.x / dot(planeNormal, planeNormal),
    y: dotPN * planeNormal.y / dot(planeNormal, planeNormal),
    z: dotPN * planeNormal.z / dot(planeNormal, planeNormal)
  })

  // Triple product checks to see if projection is on the arc
  const tripleAC = dot(cross(a, proj), planeNormal)
  const tripleCB = dot(cross(proj, b), planeNormal)

  let closest: Vector3

  if (tripleAC >= 0 && tripleCB >= 0) {
    // On the arc
    closest = normalize(proj)
  } else {
    // Outside segment: pick closer endpoint
    closest = dot(p, a) > dot(p, b) ? a : b
  }

  const { lon, lat } = cartesianToLatLon(closest)
  const distance = getGeoDistance(point.lng, point.lat, lon, lat)
  return {
    point: { lng: lon, lat },
    distance
  }
}
